/* eslint-disable no-console */
/* eslint-disable indent */
import React, { useCallback, useMemo } from "react";
import screenmediaLogo from "../../img/screenmedia-logo.png";
import visionaLogo from "../../img/visiona-logo.png";
import wecastLogo from "../../img/wecast-logo.png";
import { Document, Page, Text, View, Image } from "@react-pdf/renderer";
import styles from "./stylesPdfCotizacion";

const today = new Date();
const month = String(today.getMonth() + 1).padStart(2, "0");
const day = String(today.getDate()).padStart(2, "0");
const date = `${day}-${month}-${today.getFullYear()}`;
const hour = String(today.getHours()).padStart(2, "0");
const minute = String(today.getMinutes()).padStart(2, "0");

const PdfCotizacion = ({
  ascensoresSeleccionados = 0,
  audienciaSeleccionadaAscensor = 0,
  audienciaSeleccionadaCowork = 0,
  audienciaSeleccionadaEspera = 0,
  audienciaSeleccionadaTotal = 0,
  audienciaSeleccionadaTotems = 0,
  codigoGenerado,
  coworkSeleccionados = 0,
  cpmAscensoresFinal = 0,
  cpmCoworkFinal = 0,
  cpmEsperaFinal = 0,
  cpmTotalFinal = 0,
  cpmTotemsFinal = 0,
  descuentoGlobal = 0,
  duracion = 0,
  esperaSeleccionados = 0,
  horasFinal = 0,
  impactosTotalAscensores = 0,
  impactosTotalCowork = 0,
  impactosTotalEspera = 0,
  impactosTotalTotems = 0,
  impactosTotales = 0,
  intervalos,
  inversionAscensoresFinal = 0,
  inversionCoworkFinal = 0,
  inversionEsperaFinal = 0,
  inversionFinalTotal = 0,
  inversionTotemsFinal = 0,
  isProgrammaticSelected,
  nombresEdificiosNoSeleccionados = [],
  nombresEdificiosSeleccionadosAscensores = [],
  nombresEdificiosSeleccionadosCowork = [],
  nombresEdificiosSeleccionadosEspera = [],
  nombresEdificiosSeleccionadosTotems = [],
  includeInstallingBuildings = false,
  options = [],
  pais = "Chile",
  plazo = 0,
  salidasAscensores = 0,
  salidasCowork = 0,
  salidasEspera = 0,
  salidasTotales = 0,
  salidasTotems = 0,
  tarifaFriaAscensores = 0,
  tarifaFriaCowork = 0,
  tarifaFriaEspera = 0,
  tarifaFriaTotal = 0,
  tarifaFriaTotems = 0,
  totemsSeleccionados = 0,
  usuarioApellido,
  usuarioNombre,
  frecuencia,
  manualDiscountAmount = 0,
  finalAmount = 0,
  manualDiscountPercentage = 0,
  selectedMoneda = "",
}) => {
  const localString = useCallback(
    (pais) =>
      ({
        Chile: "es-CL",
        Perú: "es-PE",
        Uruguay: "es-UY",
      })[pais] || "es-UY",
    []
  );

  const monedaSimbolo = useCallback(
    (pais) => {
      if (selectedMoneda === "US$") return "US$ ";
      return (
        {
          Chile: "$ ",
          Perú: "S/ ",
          Uruguay: "$ ",
        }[pais] || "CLP "
      );
    },
    [selectedMoneda]
  );

  const ciudad = useCallback(
    (pais) =>
      ({
        Chile: "Santiago",
        Perú: "Lima",
        Uruguay: "Montevideo",
      })[pais] || "Lima",
    []
  );

  const operacionTextual = useCallback(
    (pais) =>
      ({
        Chile: "WECAST",
        Perú: "VISIONA",
        Uruguay: "SCREENMEDIA",
      })[pais] || "VISIONA",
    []
  );

  const monedaTextual = useCallback(
    (pais) => {
      if (selectedMoneda === "US$") return "US$"; // Forzar USD si aplica
      return (
        {
          Chile: "CLP",
          Perú: "PEN",
          Uruguay: "URU",
        }[pais] || "CLP"
      );
    },
    [selectedMoneda] // Dependencia nueva
  );

  const mailTextual = useCallback(
    (pais) =>
      ({
        Chile: "hola@wecast.cl",
        Perú: "hola@visiona.pe",
        Uruguay: "hola@screenmedia.com.uy",
      })[pais] || "hola@visiona.pe",
    []
  );

  const logo = useMemo(
    () =>
      ({
        Chile: wecastLogo,
        Perú: visionaLogo,
        Uruguay: screenmediaLogo,
      })[pais] || visionaLogo,
    [pais]
  );

  // Calcular totales
  const totalPantallas = ascensoresSeleccionados + totemsSeleccionados + coworkSeleccionados + esperaSeleccionados;
  const nombreUsuario = `${usuarioNombre} ${usuarioApellido}`;

  // Calcular el número de columnas y el ancho de cada celda
  const numberOfColumns = 6; // Always 6 columns now (with COTIZACIÓN and DESCUENTO always visible)
  const cellWidth = `${100 / numberOfColumns}%`;

  const ordenarIntervalos = (a, b) => {
    const [horaA, minutoA] = a.split(" - ")[0].split(":").map(Number);
    const [horaB, minutoB] = b.split(" - ")[0].split(":").map(Number);

    if (horaA !== horaB) {
      return horaA - horaB;
    }
    return minutoA - minutoB;
  };

  const pluralizar = (cantidad, singular, plural) => {
    if (cantidad === 0) return "ningún edificio";
    return cantidad === 1 ? singular : plural;
  };

  // Calculate total daily appearances
  const aparicionesDiarias = frecuencia && horasFinal && totalPantallas ? frecuencia * horasFinal * totalPantallas : 0;
  const aparicionesTotal = aparicionesDiarias ? aparicionesDiarias * plazo : 0;

  // Determinar circuitos activos
  const activeCircuits = [
    {
      name: "ASCENSORES",
      isActive: ascensoresSeleccionados > 0,
      impactos: impactosTotalAscensores,
      tarifa: tarifaFriaAscensores,
      baseInversion: inversionAscensoresFinal, // Usamos el base para calcular proporcional
      cpm: cpmAscensoresFinal,
    },
    {
      name: "TÓTEMS",
      isActive: totemsSeleccionados > 0,
      impactos: impactosTotalTotems,
      tarifa: tarifaFriaTotems,
      baseInversion: inversionTotemsFinal,
      cpm: cpmTotemsFinal,
    },
    {
      name: "ESPERA",
      isActive: esperaSeleccionados > 0,
      impactos: impactosTotalEspera,
      tarifa: tarifaFriaEspera,
      baseInversion: inversionEsperaFinal,
      cpm: cpmEsperaFinal,
    },
    {
      name: "COWORK iF",
      isActive: coworkSeleccionados > 0,
      impactos: impactosTotalCowork,
      tarifa: tarifaFriaCowork,
      baseInversion: inversionCoworkFinal,
      cpm: cpmCoworkFinal,
    },
  ].filter((circuit) => circuit.isActive);

  return (
    <Document>
      <Page size={[825, 650]} orientation="landscape" style={[styles.page]}>
        <View style={[styles.contentContainer, { flex: 1, justifyContent: "flex-start", height: "100%" }]}>
          <Text style={styles.header} fixed>
            ~ CONFIDENCIAL ~
          </Text>
          <Text style={styles.title}>COTIZACIÓN de ESPACIOS PUBLICITARIOS en {operacionTextual(pais)}</Text>
          <Text style={styles.letrachica0}>
            {ciudad(pais)}, {date} @ {hour}:{minute} | Generado por: {nombreUsuario} | Cotización ID: {codigoGenerado}
          </Text>

          {/* Tabla con estilos ajustados */}
          <View style={[styles.table, { width: "100%" }]}>
            <View
              style={[
                styles.tableHeaderTipo,
                {
                  backgroundColor: isProgrammaticSelected ? "#006400" : "#FF8C00",
                  alignSelf: "center",
                },
              ]}
            >
              <Text>TIPO DE COMPRA: {isProgrammaticSelected ? "PROGRAMMATIC" : "DIGITAL"}</Text>
            </View>

            <View style={[styles.tableHeaderRow, styles.tableHeader]}>
              <Text style={[styles.tableCellQuote, { width: cellWidth }]}>CIRCUITO</Text>
              <Text style={[styles.tableCellQuote, { width: cellWidth }]}>IMPACTOS</Text>
              <Text style={[styles.tableCellQuote, { width: cellWidth }]}>COTIZACIÓN</Text>
              <View style={[styles.tableCellQuote, { width: cellWidth, backgroundColor: "#FF0000" }]}>
                <Text style={([styles.tableCellQuoteColoredText], { color: "#ffffff" })}>DESCUENTO</Text>
              </View>
              <View style={[styles.tableCellQuote, { width: cellWidth, backgroundColor: "#008000" }]}>
                <Text style={([styles.tableCellQuoteColoredText], { color: "#ffffff" })}>INVERSIÓN</Text>
              </View>
              <View style={[styles.tableCellQuote, styles.lastCell, { width: cellWidth, backgroundColor: "#0000FF" }]}>
                <Text style={([styles.tableCellQuoteColoredText], { color: "#ffffff" })}>CPM</Text>
              </View>
            </View>

            {/* Renderizar solo circuitos activos */}
            {activeCircuits.map((circuit, index) => {
              // Calcular inversión con descuento manual proporcional (similar al componente principal)
              const effectiveInversion =
                manualDiscountAmount > 0 && inversionFinalTotal > 0
                  ? circuit.baseInversion - (circuit.baseInversion / inversionFinalTotal) * manualDiscountAmount
                  : circuit.baseInversion;

              return (
                <View style={styles.tableRow} key={index}>
                  <Text style={[styles.tableCellStart, styles.tableData, { width: cellWidth }]}>{circuit.name}</Text>
                  <Text style={[styles.tableCellQuote, styles.tableData, { width: cellWidth }]}>
                    {circuit.impactos.toLocaleString(localString(pais), {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </Text>
                  <Text style={[styles.tableCellQuote, styles.tableData, { width: cellWidth }]}>
                    {`${monedaSimbolo(pais)}${circuit.tarifa.toLocaleString(localString(pais), {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}`}
                  </Text>
                  <Text style={[styles.tableCell, styles.tableData, { width: cellWidth }]}>
                    {manualDiscountPercentage > 0
                      ? `${(descuentoGlobal * 100).toLocaleString(localString(pais), {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}% + ${manualDiscountPercentage.toLocaleString(localString(pais), {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}%`
                      : `${(descuentoGlobal * 100).toLocaleString(localString(pais), {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        })}%`}
                  </Text>
                  <Text style={[styles.tableCellQuote, styles.tableData, { width: cellWidth }]}>
                    {`${monedaSimbolo(pais)} ${Math.floor(effectiveInversion).toLocaleString(localString(pais), {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}`}
                  </Text>
                  <Text style={[styles.tableCell, styles.tableData, styles.lastCell, { width: cellWidth }]}>
                    {`${monedaSimbolo(pais)} ${circuit.cpm.toLocaleString(localString(pais), {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}`}
                  </Text>
                </View>
              );
            })}

            {/* Totales */}
            <View style={[styles.tableRow, styles.tableTotal]}>
              <Text style={[styles.tableCellStart, styles.tableDataTotales, { width: cellWidth }]}>TOTALES</Text>
              <Text style={[styles.tableCellQuote, styles.tableDataTotales, { width: cellWidth }]}>
                {impactosTotales.toLocaleString(localString(pais), {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }) || "-"}
              </Text>
              <Text style={[styles.tableCellQuote, styles.tableDataTotales, { width: cellWidth }]}>
                {totalPantallas
                  ? `${monedaSimbolo(pais)}${tarifaFriaTotal.toLocaleString(localString(pais), {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}`
                  : "-"}
              </Text>
              <Text style={[styles.tableCell, styles.tableDataTotales, { width: cellWidth }]}>
                {descuentoGlobal
                  ? manualDiscountPercentage > 0
                    ? `${(descuentoGlobal * 100).toLocaleString(localString(pais), {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}% + ${manualDiscountPercentage.toLocaleString(localString(pais), {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}%`
                    : `${(descuentoGlobal * 100).toLocaleString(localString(pais), {
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 1,
                      })}%`
                  : "-"}
              </Text>
              <Text style={[styles.tableCellQuote, styles.tableDataTotales, { width: cellWidth }]}>
                {`${monedaSimbolo(pais)} ${
                  manualDiscountAmount > 0
                    ? finalAmount.toLocaleString(localString(pais), {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })
                    : inversionFinalTotal.toLocaleString(localString(pais), {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })
                }`}
              </Text>
              <Text style={[styles.tableCell, styles.tableDataTotales, styles.lastCell, { width: cellWidth }]}>
                {`${monedaSimbolo(pais)} ${
                  cpmTotalFinal
                    ? cpmTotalFinal.toLocaleString(localString(pais), {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })
                    : "-"
                }`}
              </Text>
            </View>
          </View>
          <View style={styles.hr} />
          <View style={styles.detallesSeccion}>
            <Text style={styles.detallesTitulo}>PANTALLAS</Text>
            <Text style={styles.inventariosubTitulo}>{totalPantallas || "-"} en total</Text>
            <Text style={[styles.inventarioNombres]}>
              {[
                ascensoresSeleccionados ? `${ascensoresSeleccionados} pantallas en ascensores` : null,
                totemsSeleccionados ? `${totemsSeleccionados} tótems de circulación` : null,
                esperaSeleccionados ? `${esperaSeleccionados} pantallas de espera` : null,
                coworkSeleccionados ? `${coworkSeleccionados} pantallas en cowork` : null,
              ]
                .filter(Boolean)
                .join(", ")}{" "}
              {![ascensoresSeleccionados, totemsSeleccionados, esperaSeleccionados, coworkSeleccionados].some(
                Boolean
              ) && "-"}{" "}
            </Text>
          </View>
          <View style={styles.detallesSeccion}>
            <Text style={styles.detallesTitulo}>AUDIENCIA</Text>
            <Text style={styles.detallesText}>
              {audienciaSeleccionadaTotal.toLocaleString(localString(pais), {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }) || "-"}{" "}
              personas únicas
            </Text>
          </View>
          <View style={styles.detallesSeccion}>
            <Text style={styles.detallesTitulo}>PLAZO DE PAUTA</Text>
            <Text style={styles.detallesText}>{plazo || "-"} días (lunes a viernes)</Text>
          </View>
          <View style={styles.detallesSeccion}>
            <Text style={styles.detallesTitulo}>EXHIBICIÓN</Text>
            <Text style={styles.detallesText}>
              {intervalos.length > 0
                ? `Intervalos: ${intervalos.sort(ordenarIntervalos).join(" | ")} = ${horasFinal || "-"} horas por día`
                : options.length > 0
                  ? `${options[0].label} = ${horasFinal || "-"} horas por día`
                  : `No hay opciones seleccionadas, ${horasFinal || "-"} horas por día`}
            </Text>
          </View>
          <View style={styles.detallesSeccion}>
            <Text style={styles.detallesTitulo}>FRECUENCIA</Text>
            {frecuencia ? (
              <>
                <Text style={styles.detallesText}>
                  Por hora: {frecuencia} reproducciones por pantalla,{" "}
                  {(frecuencia * totalPantallas).toLocaleString(localString(pais), {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}{" "}
                  totales
                </Text>
                <Text style={styles.detallesText}>
                  Por día:{" "}
                  {aparicionesDiarias
                    ? aparicionesDiarias.toLocaleString(localString(pais), {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }) + " reproducciones"
                    : "- reproducciones"}
                </Text>
                <Text style={styles.detallesText}>
                  Por campaña:{" "}
                  {aparicionesTotal
                    ? aparicionesTotal.toLocaleString(localString(pais), {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }) + " reproducciones"
                    : "- reproducciones"}
                </Text>
              </>
            ) : (
              <Text style={styles.detallesText}>No especificada</Text>
            )}
          </View>
          <View style={styles.detallesSeccion}>
            <Text style={styles.detallesTitulo}>DURACIÓN DEL CONTENIDO</Text>
            <Text style={styles.detallesText}>{duracion || "-"} segundos</Text>
          </View>
          <View style={styles.detallesSeccion}>
            <Text style={styles.detallesTitulo}>INVENTARIO</Text>
            <Text style={[styles.inventariosubTitulo]}>
              {(() => {
                const todosSeleccionados = [
                  ...nombresEdificiosSeleccionadosAscensores,
                  ...nombresEdificiosSeleccionadosTotems,
                  ...nombresEdificiosSeleccionadosEspera,
                  ...nombresEdificiosSeleccionadosCowork,
                ];
                const edificiosSeleccionadosUnicos = [...new Set(todosSeleccionados)].sort();
                return `${edificiosSeleccionadosUnicos.length} ${pluralizar(
                  edificiosSeleccionadosUnicos.length,
                  "edificio seleccionado",
                  "edificios seleccionados"
                )}`;
              })()}
            </Text>
            {(() => {
              const todosSeleccionados = [
                ...nombresEdificiosSeleccionadosAscensores,
                ...nombresEdificiosSeleccionadosTotems,
                ...nombresEdificiosSeleccionadosEspera,
                ...nombresEdificiosSeleccionadosCowork,
              ];
              const edificiosSeleccionadosUnicos = [...new Set(todosSeleccionados)].sort();
              return edificiosSeleccionadosUnicos.length > 0 ? (
                <Text style={[styles.inventarioNombres]}>{edificiosSeleccionadosUnicos.join(", ")}</Text>
              ) : null;
            })()}
            {(() => {
              if (nombresEdificiosNoSeleccionados.length > 0) {
                return (
                  <>
                    <Text style={[styles.inventariosubTitulo]}>
                      {`${nombresEdificiosNoSeleccionados.length} ${pluralizar(
                        nombresEdificiosNoSeleccionados.length,
                        "edificio no seleccionado",
                        "edificios no seleccionados"
                      )}`}
                    </Text>
                    <Text style={[styles.inventarioNombres]}>{nombresEdificiosNoSeleccionados.sort().join(", ")}</Text>
                  </>
                );
              }
              return null;
            })()}
          </View>
          <View style={styles.hr} />
          <View style={[styles.footer, { marginTop: 0 }]}>
            <Text style={styles.letrachica2}>
              Propuesta exclusiva para agencia y/o cliente cotizado. Importes expresados en {monedaTextual(pais)} y no
              incluyen impuestos. Exhibición de lunes a viernes de 7 AM a 9 PM según frecuencia y horario contratado.
              Propuesta incluye descuentos especiales que no podrán considerarse como antecedente. El cliente podrá
              cambiar materiales tantas veces como desee. Cotización válida para pautas a emitirse hasta diciembre 2025
              inclusive.
            </Text>
            <Image style={styles.image} src={logo} />
            <Text style={styles.mail}>{mailTextual(pais)}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default PdfCotizacion;
